import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ArrowRight, ArrowLeft, FileSpreadsheet, BarChart3, BarChart, RefreshCcw, FileText, Briefcase, DollarSign, Building, Car, Home, CreditCard, UserCog, Bot, FileSearch, Clock, Landmark, PiggyBank, Wallet } from "lucide-react";
import { motion } from "framer-motion";

export default function Operations() {
  // Estado para controlar qual operação está selecionada
  const [selectedOperation, setSelectedOperation] = useState(null);
  // Estado para controlar o histórico de navegação
  const [navigationHistory, setNavigationHistory] = useState([]);

  // Lista de operações disponíveis
  const operationsData = [
    {
      id: "google-sheets-questor",
      title: "Google Sheets For Questor",
      description: "Integração com Google Sheets para importação e exportação de dados do Questor",
      icon: <FileSpreadsheet className="w-12 h-12 text-green-600" />,
      color: "bg-green-50 border-green-200",
      textColor: "text-green-700",
      url: createPageUrl("GoogleSheetsQuestor")
    },
    {
      id: "annual-revenue",
      title: "Annual Revenue",
      description: "Distribua o faturamento anual em 12 meses para planejamento financeiro",
      icon: <BarChart3 className="w-12 h-12 text-indigo-600" />,
      color: "bg-indigo-50 border-indigo-200",
      textColor: "text-indigo-700",
      url: null, // Removemos a URL direta para tratar o clique de forma especial
      subOperations: [
        {
          id: "annual-revenue-last-month",
          title: "Annual Revenue Last Month",
          description: "Calcule o faturamento dos últimos 12 meses",
          icon: <BarChart className="w-12 h-12 text-indigo-600" />,
          color: "bg-indigo-50 border-indigo-200",
          textColor: "text-indigo-700",
          url: createPageUrl("AnnualRevenue")
        },
        {
          id: "update-annual-revenue",
          title: "Update Annual Revenue",
          description: "Atualize o faturamento com base em arquivo CSV existente",
          icon: <RefreshCcw className="w-12 h-12 text-purple-600" />,
          color: "bg-purple-50 border-purple-200",
          textColor: "text-purple-700",
          url: createPageUrl("UpdateAnnualRevenue")
        },
        {
          id: "variavel-revenue",
          title: "Dinamic Revenue",
          description: "Distribuição dinâmica de faturamento por quantidade variável de meses (1-12)",
          icon: <DollarSign className="w-12 h-12 text-emerald-600" />,
          color: "bg-emerald-50 border-emerald-200",
          textColor: "text-emerald-700",
          url: createPageUrl("VariavelRevenue")
        }
      ]
    },
    {
      id: "agent",
      title: "Agent",
      description: "Assistente inteligente para automação de tarefas",
      icon: <UserCog className="w-12 h-12 text-teal-600" />,
      color: "bg-teal-50 border-teal-200",
      textColor: "text-teal-700",
      url: null, // URL direta removida para tratar o clique de forma especial
      subOperations: [
        {
          id: "agent-irpf",
          title: "Agent-IRPF",
          description: "Assistente para automação de tarefas relacionadas ao IRPF",
          icon: <Bot className="w-12 h-12 text-teal-600" />,
          color: "bg-teal-50 border-teal-200",
          textColor: "text-teal-700",
          url: null, // Removida URL direta para adicionar subcards
          subOperations: [
            {
              id: "agent-rendimentos-pf",
              title: "Agent - Rendimentos PF",
              description: "Assistente para automação de rendimentos de pessoa física",
              icon: <UserCog className="w-12 h-12 text-blue-600" />,
              color: "bg-blue-50 border-blue-200",
              textColor: "text-blue-700",
              url: createPageUrl("AgentRendPF")
            },
            {
              id: "agent-bens-direitos",
              title: "Agent - Bens e Direitos",
              description: "Assistente para automação de bens e direitos",
              icon: <Briefcase className="w-12 h-12 text-orange-600" />,
              color: "bg-orange-50 border-orange-200",
              textColor: "text-orange-700",
              url: createPageUrl("AgentIrpf")
            }
          ]
        },
        {
          id: "agent-questor",
          title: "Agent-Questor",
          description: "Assistente para automação de tarefas relacionadas ao Questor",
          icon: <FileSearch className="w-12 h-12 text-cyan-600" />,
          color: "bg-cyan-50 border-cyan-200",
          textColor: "text-cyan-700",
          url: createPageUrl("AgentQuestor")
        }
      ]
    },
    {
      id: "irpf",
      title: "IRPF",
      description: "Gerenciamento de informações para Imposto de Renda Pessoa Física",
      icon: <FileText className="w-12 h-12 text-sky-600" />,
      color: "bg-sky-50 border-sky-200",
      textColor: "text-sky-700",
      url: null, // URL direta removida para tratar o clique de forma especial
      subOperations: [
        {
          id: "bens-e-direitos",
          title: "Bens e Direitos",
          description: "Gerencie seus bens e direitos para declaração do IRPF",
          icon: <Briefcase className="w-12 h-12 text-orange-600" />,
          color: "bg-orange-50 border-orange-200",
          textColor: "text-orange-700",
          url: null, // Alterado para null para tratar o clique especial
          subOperations: [
            {
              id: "empresa",
              title: "Empresa",
              description: "Gerenciar dados de participações societárias e empresas",
              icon: <Building className="w-12 h-12 text-purple-600" />,
              color: "bg-purple-50 border-purple-200",
              textColor: "text-purple-700",
              url: createPageUrl("BensEmpresa")
            },
            {
              id: "veiculos",
              title: "Veículos",
              description: "Gerenciar dados de veículos e outros bens móveis",
              icon: <Car className="w-12 h-12 text-emerald-600" />,
              color: "bg-emerald-50 border-emerald-200",
              textColor: "text-emerald-700",
              url: createPageUrl("BensVeiculos")
            },
            {
              id: "imoveis",
              title: "Imóveis",
              description: "Gerenciar dados de imóveis e propriedades",
              icon: <Home className="w-12 h-12 text-rose-600" />,
              color: "bg-rose-50 border-rose-200",
              textColor: "text-rose-700",
              url: createPageUrl("IrpfImoveis")
            },
            {
              id: "informes-bancarios",
              title: "Informes Bancários",
              description: "Gerenciar dados de contas e aplicações financeiras",
              icon: <CreditCard className="w-12 h-12 text-amber-600" />,
              color: "bg-amber-50 border-amber-200",
              textColor: "text-amber-700",
              url: createPageUrl("IrpfInformesBancarios")
            }
          ]
        },
        {
          id: "rendimentos-recebidos",
          title: "Rendimentos Recebidos",
          description: "Gerencie seus rendimentos recebidos para declaração do IRPF",
          icon: <DollarSign className="w-12 h-12 text-emerald-600" />,
          color: "bg-emerald-50 border-emerald-200",
          textColor: "text-emerald-700",
          url: null, // Alterado para null para tratar o clique especial
          subOperations: [
            {
              id: "rendimento-pessoa-fisica",
              title: "Rendimento Pessoa Física",
              description: "Gerenciar dados de rendimentos recebidos de pessoas físicas",
              icon: <UserCog className="w-12 h-12 text-blue-600" />,
              color: "bg-blue-50 border-blue-200",
              textColor: "text-blue-700",
              url: createPageUrl("RendimentoPessoaFisica")
            },
            {
              id: "rendimento-pessoa-juridica",
              title: "Rendimento Pessoa Jurídica",
              description: "Gerenciar dados de rendimentos recebidos de pessoas jurídicas",
              icon: <Building className="w-12 h-12 text-violet-600" />,
              color: "bg-violet-50 border-violet-200",
              textColor: "text-violet-700",
              url: createPageUrl("RendimentoPessoaJuridica")
            }
          ]
        },
        {
          id: "pagamentos-efetuados",
          title: "Pagamentos Efetuados",
          description: "Gerencie seus pagamentos efetuados para declaração do IRPF",
          icon: <CreditCard className="w-12 h-12 text-red-600" />,
          color: "bg-red-50 border-red-200",
          textColor: "text-red-700",
          url: createPageUrl("PagamentosEfetuados")
        }
      ]
    },
    {
      id: "pro-labore",
      title: "Pró-Labore",
      description: "Gerenciamento de informações para Pró-Labore",
      icon: <Briefcase className="w-12 h-12 text-purple-600" />,
      color: "bg-purple-50 border-purple-200",
      textColor: "text-purple-700",
      url: createPageUrl("ProLabore")
    },
    {
      id: "extrato",
      title: "Extrato $",
      description: "Gerenciamento de extratos bancários",
      icon: <CreditCard className="w-12 h-12 text-lime-600" />,
      color: "bg-lime-50 border-lime-200",
      textColor: "text-lime-700",
      url: createPageUrl("Extrato")
    },
    {
      id: "credit-card",
      title: "Credit Card",
      description: "Análise de despesas pessoais do cartão de crédito",
      icon: <CreditCard className="w-12 h-12 text-pink-600" />,
      color: "bg-pink-50 border-pink-200",
      textColor: "text-pink-700",
      url: createPageUrl("CreditCard")
    },
    {
      id: "controle-ponto",
      title: "Controle de Ponto",
      description: "Gerenciamento e controle de ponto dos funcionários",
      icon: <Clock className="w-12 h-12 text-blue-600" />,
      color: "bg-blue-50 border-blue-200",
      textColor: "text-blue-700",
      url: createPageUrl("ControlePonto")
    },
    {
      id: "contabil-questor",
      title: "Contábil Questor",
      description: "Ferramentas contábeis integradas com Questor",
      icon: <FileText className="w-12 h-12 text-slate-600" />,
      color: "bg-slate-50 border-slate-200",
      textColor: "text-slate-700",
      url: null, // URL direta removida para tratar o clique de forma especial
      subOperations: [
        {
          id: "zendry-sa",
          title: "Zendry S.A",
          description: "Ferramentas contábeis para Zendry S.A",
          icon: <Building className="w-12 h-12 text-blue-600" />,
          color: "bg-blue-50 border-blue-200",
          textColor: "text-blue-700",
          url: null, // URL direta removida para tratar o clique de forma especial
          subOperations: [
            {
              id: "conta-receita-zendry",
              title: "Conta Receita - Zendry",
              description: "Gerenciamento de contas de receita no sistema Zendry",
              icon: <DollarSign className="w-12 h-12 text-green-600" />,
              color: "bg-green-50 border-green-200",
              textColor: "text-green-700",
              url: createPageUrl("ContaReceitaZendry")
            },
            {
              id: "contas-a-pagar-zendry",
              title: "Contas a Pagar - Zendry",
              description: "Gerenciamento de contas a pagar no sistema Zendry",
              icon: <FileText className="w-12 h-12 text-orange-600" />,
              color: "bg-orange-50 border-orange-200",
              textColor: "text-orange-700",
              url: createPageUrl("ContasaPagarZendry")
            },
            {
              id: "despesas-geral-zendry",
              title: "Despesas Geral - Zendry",
              description: "Gerenciamento de despesas gerais no sistema Zendry",
              icon: <CreditCard className="w-12 h-12 text-red-600" />,
              color: "bg-red-50 border-red-200",
              textColor: "text-red-700",
              url: createPageUrl("DespesasGeralZendry")
            },
            {
              id: "c6-bank",
              title: "C6 Bank",
              description: "Gerenciamento de operações bancárias C6 Bank",
              icon: <CreditCard className="w-12 h-12 text-purple-600" />,
              color: "bg-purple-50 border-purple-200",
              textColor: "text-purple-700",
              url: createPageUrl("C6Bank")
            }
          ]
        },
        {
          id: "rjx-servicos",
          title: "RJX Serviços",
          description: "Ferramentas contábeis para RJX Serviços",
          icon: <Briefcase className="w-12 h-12 text-purple-600" />,
          color: "bg-purple-50 border-purple-200",
          textColor: "text-purple-700",
          url: null, // URL direta removida para tratar o clique de forma especial
          subOperations: [
            {
              id: "tov-solucoes",
              title: "TOV SOLUCOES",
              description: "Gerenciamento de operações contábeis TOV Soluções",
              icon: <DollarSign className="w-12 h-12 text-green-600" />,
              color: "bg-green-50 border-green-200",
              textColor: "text-green-700",
              url: createPageUrl("TovSolucoes")
            },
            {
              id: "banco-do-brasil",
              title: "BANCO DO BRASIL",
              description: "Gerenciamento de operações contábeis Banco do Brasil",
              icon: <CreditCard className="w-12 h-12 text-yellow-600" />,
              color: "bg-yellow-50 border-yellow-200",
              textColor: "text-yellow-700",
              url: createPageUrl("BancoDoBrasilRJX")
            },
            {
              id: "fibank",
              title: "FIBANK",
              description: "Gerenciamento de operações contábeis FIBANK",
              icon: <FileText className="w-12 h-12 text-orange-600" />,
              color: "bg-orange-50 border-orange-200",
              textColor: "text-orange-700",
              url: createPageUrl("FibankRJX")
            }
          ]
        },
        {
          id: "soft-solutions",
          title: "Soft Solutions",
          description: "Ferramentas contábeis para Soft Solutions",
          icon: <Building className="w-12 h-12 text-teal-600" />,
          color: "bg-teal-50 border-teal-200",
          textColor: "text-teal-700",
          url: null, // URL direta removida para tratar o clique de forma especial
          subOperations: [
            {
              id: "soft-santander",
              title: "Santander",
              description: "Gerenciamento de operações contábeis Santander",
              icon: <Landmark className="w-12 h-12 text-red-600" />,
              color: "bg-red-50 border-red-200",
              textColor: "text-red-700",
              url: createPageUrl("SoftSantander")
            },
            {
              id: "soft-unicred",
              title: "Unicred",
              description: "Gerenciamento de operações contábeis Unicred",
              icon: <PiggyBank className="w-12 h-12 text-green-600" />,
              color: "bg-green-50 border-green-200",
              textColor: "text-green-700",
              url: createPageUrl("SoftUnicred")
            },
            {
              id: "soft-bradesco",
              title: "Bradesco",
              description: "Gerenciamento de operações contábeis Bradesco",
              icon: <Wallet className="w-12 h-12 text-orange-600" />,
              color: "bg-orange-50 border-orange-200",
              textColor: "text-orange-700",
              url: createPageUrl("SoftBradesco")
            }
          ]
        }
      ]
    }
    // Aqui podem ser adicionadas mais operações no futuro
  ].map((operation, index) => ({ ...operation, __originalIndex: index }));

  const operationsOrderMap = {
    "google-sheets-questor": 0,
    "annual-revenue": 1,
    "contabil-questor": 2,
    "controle-ponto": 3,
    "credit-card": 4,
    "extrato": 5,
    "pro-labore": 6,
    "irpf": 7
  };

  const operations = operationsData
    .sort((a, b) => {
      const orderA = operationsOrderMap[a.id] ?? (100 + a.__originalIndex);
      const orderB = operationsOrderMap[b.id] ?? (100 + b.__originalIndex);
      return orderA - orderB;
    })
    .map(({ __originalIndex, ...operation }) => operation);

  // Função para lidar com o clique em uma operação
  const handleOperationClick = (operationId) => {
    if (operationId === "annual-revenue" || operationId === "irpf" || operationId === "agent" || operationId === "contabil-questor") {
      setSelectedOperation(operationId);
      // Adiciona ao histórico de navegação
      setNavigationHistory([...navigationHistory, operationId]);
    }
  };

  // Função para lidar com o clique em uma sub-operação
  const handleSubOperationClick = (operationId, subOperationId) => {
    setSelectedOperation(subOperationId);
    // Adiciona ao histórico de navegação
    setNavigationHistory([...navigationHistory, subOperationId]);
  };

  // Função para voltar para o card anterior
  const handleGoBack = () => {
    if (navigationHistory.length > 0) {
      // Remove o último item do histórico
      const newHistory = [...navigationHistory];
      newHistory.pop();
      setNavigationHistory(newHistory);
      
      // Define a operação selecionada como o último item do histórico atualizado
      if (newHistory.length === 0) {
        setSelectedOperation(null);
      } else {
        setSelectedOperation(newHistory[newHistory.length - 1]);
      }
    } else {
      // Se não houver histórico, volta para a tela inicial
      setSelectedOperation(null);
    }
  };

  return (
    <div className="container mx-auto">
      <header className="mb-8">
        <div className="flex items-center mb-2">
          <Button 
            variant="ghost" 
            onClick={handleGoBack} 
            className={`p-2 mr-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors ${navigationHistory.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={navigationHistory.length === 0}
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="sr-only">Voltar</span>
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Operações</h1>
        </div>
        <p className="text-gray-500 ml-11">
          Selecione uma das operações disponíveis para começar
        </p>
      </header>

      {selectedOperation === "annual-revenue" ? (
        <div className="flex flex-col items-center">
          {/* Card Annual Revenue centralizado */}
          <motion.div
            key="annual-revenue"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md mx-auto"
          >
            {operations.find(op => op.id === "annual-revenue") && (
              <Card 
                className={`h-full border-2 shadow-lg transition-all duration-200 ${operations.find(op => op.id === "annual-revenue").color} border-indigo-500`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">
                      {operations.find(op => op.id === "annual-revenue").icon}
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${operations.find(op => op.id === "annual-revenue").textColor}`}>
                      {operations.find(op => op.id === "annual-revenue").title}
                    </h2>
                    <p className="text-gray-600 mb-4">{operations.find(op => op.id === "annual-revenue").description}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Sub-operações abaixo do card principal */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mt-8 w-full"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Opções de Faturamento</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {operations.find(op => op.id === "annual-revenue").subOperations.map((subOp) => (
                <motion.div
                  key={subOp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link to={subOp.url} className="block h-full">
                    <Card className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subOp.color}`}>
                      <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4">
                            {subOp.icon}
                          </div>
                          <h2 className={`text-xl font-bold mb-2 ${subOp.textColor}`}>{subOp.title}</h2>
                          <p className="text-gray-600 mb-4">{subOp.description}</p>
                          <Button variant="ghost" className={`gap-2 ${subOp.textColor}`}>
                            Acessar <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      ) : selectedOperation === "agent" ? (
        <div className="flex flex-col items-center">
          {/* Card Agent centralizado */}
          <motion.div
            key="agent"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md mx-auto"
          >
            {operations.find(op => op.id === "agent") && (
              <Card 
                className={`h-full border-2 shadow-lg transition-all duration-200 ${operations.find(op => op.id === "agent").color} border-teal-500`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">
                      {operations.find(op => op.id === "agent").icon}
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${operations.find(op => op.id === "agent").textColor}`}>
                      {operations.find(op => op.id === "agent").title}
                    </h2>
                    <p className="text-gray-600 mb-4">{operations.find(op => op.id === "agent").description}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Sub-operações abaixo do card principal */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mt-8 w-full"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Opções de Agent</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {operations.find(op => op.id === "agent").subOperations.map((subOp) => (
                <motion.div
                  key={subOp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {subOp.url ? (
                    <Link to={subOp.url} className="block h-full">
                      <Card className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subOp.color}`}>
                        <CardContent className="pt-6 pb-6">
                          <div className="flex flex-col items-center text-center">
                            <div className="mb-4">
                              {subOp.icon}
                            </div>
                            <h2 className={`text-xl font-bold mb-2 ${subOp.textColor}`}>{subOp.title}</h2>
                            <p className="text-gray-600 mb-4">{subOp.description}</p>
                            <Button variant="ghost" className={`gap-2 ${subOp.textColor}`}>
                              Acessar <ArrowRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ) : (
                    <Card 
                      className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subOp.color} cursor-pointer`}
                      onClick={() => handleSubOperationClick("agent", subOp.id)}
                    >
                      <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4">
                            {subOp.icon}
                          </div>
                          <h2 className={`text-xl font-bold mb-2 ${subOp.textColor}`}>{subOp.title}</h2>
                          <p className="text-gray-600 mb-4">{subOp.description}</p>
                          <Button variant="ghost" className={`gap-2 ${subOp.textColor}`}>
                            Selecionar <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      ) : selectedOperation === "agent-irpf" ? (
        <div className="flex flex-col items-center">
          {/* Card Agent-IRPF centralizado */}
          <motion.div
            key="agent-irpf"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md mx-auto"
          >
            {operations.find(op => op.id === "agent").subOperations.find(subOp => subOp.id === "agent-irpf") && (
              <Card 
                className={`h-full border-2 shadow-lg transition-all duration-200 ${operations.find(op => op.id === "agent").subOperations.find(subOp => subOp.id === "agent-irpf").color} border-teal-500`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">
                      {operations.find(op => op.id === "agent").subOperations.find(subOp => subOp.id === "agent-irpf").icon}
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${operations.find(op => op.id === "agent").subOperations.find(subOp => subOp.id === "agent-irpf").textColor}`}>
                      {operations.find(op => op.id === "agent").subOperations.find(subOp => subOp.id === "agent-irpf").title}
                    </h2>
                    <p className="text-gray-600 mb-4">{operations.find(op => op.id === "agent").subOperations.find(subOp => subOp.id === "agent-irpf").description}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Sub-operações abaixo do card principal */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mt-8 w-full"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Opções de Agent-IRPF</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {operations.find(op => op.id === "agent").subOperations.find(subOp => subOp.id === "agent-irpf").subOperations.map((subOp) => (
                <motion.div
                  key={subOp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link to={subOp.url} className="block h-full">
                    <Card className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subOp.color}`}>
                      <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4">
                            {subOp.icon}
                          </div>
                          <h2 className={`text-xl font-bold mb-2 ${subOp.textColor}`}>{subOp.title}</h2>
                          <p className="text-gray-600 mb-4">{subOp.description}</p>
                          <Button variant="ghost" className={`gap-2 ${subOp.textColor}`}>
                            Acessar <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      ) : selectedOperation === "irpf" ? (
        <div className="flex flex-col items-center">
          {/* Card IRPF centralizado */}
          <motion.div
            key="irpf"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md mx-auto"
          >
            {operations.find(op => op.id === "irpf") && (
              <Card 
                className={`h-full border-2 shadow-lg transition-all duration-200 ${operations.find(op => op.id === "irpf").color} border-sky-500`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">
                      {operations.find(op => op.id === "irpf").icon}
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${operations.find(op => op.id === "irpf").textColor}`}>
                      {operations.find(op => op.id === "irpf").title}
                    </h2>
                    <p className="text-gray-600 mb-4">{operations.find(op => op.id === "irpf").description}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Sub-operações abaixo do card principal */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mt-8 w-full"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Opções de IRPF</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {operations.find(op => op.id === "irpf").subOperations.map((subOp) => (
                <motion.div
                  key={subOp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {subOp.url ? (
                    <Link to={subOp.url} className="block h-full">
                      <Card className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subOp.color}`}>
                        <CardContent className="pt-6 pb-6">
                          <div className="flex flex-col items-center text-center">
                            <div className="mb-4">
                              {subOp.icon}
                            </div>
                            <h2 className={`text-xl font-bold mb-2 ${subOp.textColor}`}>{subOp.title}</h2>
                            <p className="text-gray-600 mb-4">{subOp.description}</p>
                            <Button variant="ghost" className={`gap-2 ${subOp.textColor}`}>
                              Acessar <ArrowRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ) : (
                    <Card 
                      className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subOp.color} cursor-pointer`}
                      onClick={() => handleSubOperationClick("irpf", subOp.id)}
                    >
                      <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4">
                            {subOp.icon}
                          </div>
                          <h2 className={`text-xl font-bold mb-2 ${subOp.textColor}`}>{subOp.title}</h2>
                          <p className="text-gray-600 mb-4">{subOp.description}</p>
                          <Button variant="ghost" className={`gap-2 ${subOp.textColor}`}>
                            Selecionar <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      ) : selectedOperation === "bens-e-direitos" ? (
        <div className="flex flex-col items-center">
          {/* Card Bens e Direitos centralizado */}
          <motion.div
            key="bens-e-direitos"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md mx-auto"
          >
            {operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "bens-e-direitos") && (
              <Card 
                className={`h-full border-2 shadow-lg transition-all duration-200 ${operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "bens-e-direitos").color} border-orange-500`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">
                      {operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "bens-e-direitos").icon}
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "bens-e-direitos").textColor}`}>
                      {operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "bens-e-direitos").title}
                    </h2>
                    <p className="text-gray-600 mb-4">{operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "bens-e-direitos").description}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Sub-operações abaixo do card principal */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mt-8 w-full"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Opções de Bens e Direitos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "bens-e-direitos").subOperations.map((subOp) => (
                <motion.div
                  key={subOp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link to={subOp.url} className="block h-full">
                    <Card className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subOp.color}`}>
                      <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4">
                            {subOp.icon}
                          </div>
                          <h2 className={`text-xl font-bold mb-2 ${subOp.textColor}`}>{subOp.title}</h2>
                          <p className="text-gray-600 mb-4">{subOp.description}</p>
                          <Button variant="ghost" className={`gap-2 ${subOp.textColor}`}>
                            Acessar <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      ) : selectedOperation === "rendimentos-recebidos" ? (
        <div className="flex flex-col items-center">
          {/* Card Rendimentos Recebidos centralizado */}
          <motion.div
            key="rendimentos-recebidos"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md mx-auto"
          >
            {operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "rendimentos-recebidos") && (
              <Card 
                className={`h-full border-2 shadow-lg transition-all duration-200 ${operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "rendimentos-recebidos").color} border-emerald-500`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">
                      {operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "rendimentos-recebidos").icon}
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "rendimentos-recebidos").textColor}`}>
                      {operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "rendimentos-recebidos").title}
                    </h2>
                    <p className="text-gray-600 mb-4">{operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "rendimentos-recebidos").description}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Sub-operações abaixo do card principal */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mt-8 w-full"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Opções de Rendimentos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {operations.find(op => op.id === "irpf").subOperations.find(subOp => subOp.id === "rendimentos-recebidos").subOperations.map((subOp) => (
                <motion.div
                  key={subOp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link to={subOp.url} className="block h-full">
                    <Card className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subOp.color}`}>
                      <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4">
                            {subOp.icon}
                          </div>
                          <h2 className={`text-xl font-bold mb-2 ${subOp.textColor}`}>{subOp.title}</h2>
                          <p className="text-gray-600 mb-4">{subOp.description}</p>
                          <Button variant="ghost" className={`gap-2 ${subOp.textColor}`}>
                            Acessar <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      ) : selectedOperation === "contabil-questor" ? (
        <div className="flex flex-col items-center">
          {/* Card Contábil Questor centralizado */}
          <motion.div
            key="contabil-questor"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md mx-auto"
          >
            {operations.find(op => op.id === "contabil-questor") && (
              <Card 
                className={`h-full border-2 shadow-lg transition-all duration-200 ${operations.find(op => op.id === "contabil-questor").color} border-slate-500`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">
                      {operations.find(op => op.id === "contabil-questor").icon}
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${operations.find(op => op.id === "contabil-questor").textColor}`}>
                      {operations.find(op => op.id === "contabil-questor").title}
                    </h2>
                    <p className="text-gray-600 mb-4">{operations.find(op => op.id === "contabil-questor").description}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Sub-operações abaixo do card principal */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mt-8 w-full"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Opções de Contábil Questor</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {operations.find(op => op.id === "contabil-questor").subOperations.map((subOp) => (
                <motion.div
                  key={subOp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {subOp.url ? (
                    <Link to={subOp.url} className="block h-full">
                      <Card className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subOp.color}`}>
                        <CardContent className="pt-6 pb-6">
                          <div className="flex flex-col items-center text-center">
                            <div className="mb-4">
                              {subOp.icon}
                            </div>
                            <h2 className={`text-xl font-bold mb-2 ${subOp.textColor}`}>{subOp.title}</h2>
                            <p className="text-gray-600 mb-4">{subOp.description}</p>
                            <Button variant="ghost" className={`gap-2 ${subOp.textColor}`}>
                              Acessar <ArrowRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ) : (
                    <Card 
                      className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subOp.color} cursor-pointer`}
                      onClick={() => handleSubOperationClick("contabil-questor", subOp.id)}
                    >
                      <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4">
                            {subOp.icon}
                          </div>
                          <h2 className={`text-xl font-bold mb-2 ${subOp.textColor}`}>{subOp.title}</h2>
                          <p className="text-gray-600 mb-4">{subOp.description}</p>
                          <Button variant="ghost" className={`gap-2 ${subOp.textColor}`}>
                            Selecionar <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      ) : selectedOperation === "zendry-sa" ? (
        <div className="flex flex-col items-center">
          {/* Card Zendry S.A centralizado */}
          <motion.div
            key="zendry-sa"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md mx-auto"
          >
            {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "zendry-sa") && (
              <Card 
                className={`h-full border-2 shadow-lg transition-all duration-200 ${operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "zendry-sa").color} border-blue-500`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">
                      {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "zendry-sa").icon}
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "zendry-sa").textColor}`}>
                      {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "zendry-sa").title}
                    </h2>
                    <p className="text-gray-600 mb-4">{operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "zendry-sa").description}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Sub-operações abaixo do card principal */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mt-8 w-full"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Opções de Zendry S.A</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "zendry-sa").subOperations.map((subSubOp) => (
                <motion.div
                  key={subSubOp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link to={subSubOp.url} className="block h-full">
                    <Card className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subSubOp.color}`}>
                      <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4">
                            {subSubOp.icon}
                          </div>
                          <h2 className={`text-xl font-bold mb-2 ${subSubOp.textColor}`}>{subSubOp.title}</h2>
                          <p className="text-gray-600 mb-4">{subSubOp.description}</p>
                          <Button variant="ghost" className={`gap-2 ${subSubOp.textColor}`}>
                            Acessar <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      ) : selectedOperation === "rjx-servicos" ? (
        <div className="flex flex-col items-center">
          {/* Card RJX Serviços centralizado */}
          <motion.div
            key="rjx-servicos"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md mx-auto"
          >
            {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "rjx-servicos") && (
              <Card 
                className={`h-full border-2 shadow-lg transition-all duration-200 ${operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "rjx-servicos").color} border-purple-500`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">
                      {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "rjx-servicos").icon}
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "rjx-servicos").textColor}`}>
                      {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "rjx-servicos").title}
                    </h2>
                    <p className="text-gray-600 mb-4">{operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "rjx-servicos").description}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Sub-operações abaixo do card principal */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mt-8 w-full"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Opções de RJX Serviços</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "rjx-servicos").subOperations.map((subSubOp) => (
                <motion.div
                  key={subSubOp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link to={subSubOp.url} className="block h-full">
                    <Card className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subSubOp.color}`}>
                      <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4">
                            {subSubOp.icon}
                          </div>
                          <h2 className={`text-xl font-bold mb-2 ${subSubOp.textColor}`}>{subSubOp.title}</h2>
                          <p className="text-gray-600 mb-4">{subSubOp.description}</p>
                          <Button variant="ghost" className={`gap-2 ${subSubOp.textColor}`}>
                            Acessar <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      ) : selectedOperation === "soft-solutions" ? (
        <div className="flex flex-col items-center">
          {/* Card Soft Solutions centralizado */}
          <motion.div
            key="soft-solutions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md mx-auto"
          >
            {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "soft-solutions") && (
              <Card 
                className={`h-full border-2 shadow-lg transition-all duration-200 ${operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "soft-solutions").color} border-teal-500`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">
                      {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "soft-solutions").icon}
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "soft-solutions").textColor}`}>
                      {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "soft-solutions").title}
                    </h2>
                    <p className="text-gray-600 mb-4">{operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "soft-solutions").description}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Sub-operações abaixo do card principal */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mt-8 w-full"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Opções de Soft Solutions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {operations.find(op => op.id === "contabil-questor").subOperations.find(subOp => subOp.id === "soft-solutions").subOperations.map((subSubOp) => (
                <motion.div
                  key={subSubOp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Link to={subSubOp.url} className="block h-full">
                    <Card className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${subSubOp.color}`}>
                      <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4">
                            {subSubOp.icon}
                          </div>
                          <h2 className={`text-xl font-bold mb-2 ${subSubOp.textColor}`}>{subSubOp.title}</h2>
                          <p className="text-gray-600 mb-4">{subSubOp.description}</p>
                          <Button variant="ghost" className={`gap-2 ${subSubOp.textColor}`}>
                            Acessar <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {operations.map((operation) => (
            <motion.div
              key={operation.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {operation.url ? (
                <Link to={operation.url} className="block h-full">
                  <Card className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${operation.color}`}>
                    <CardContent className="pt-6 pb-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="mb-4">
                          {operation.icon}
                        </div>
                        <h2 className={`text-xl font-bold mb-2 ${operation.textColor}`}>{operation.title}</h2>
                        <p className="text-gray-600 mb-4">{operation.description}</p>
                        <Button variant="ghost" className={`gap-2 ${operation.textColor}`}>
                          Acessar <ArrowRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ) : (
                <Card 
                  className={`h-full border-2 hover:shadow-lg transition-all duration-200 ${operation.color} cursor-pointer`}
                  onClick={() => handleOperationClick(operation.id)}
                >
                  <CardContent className="pt-6 pb-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="mb-4">
                        {operation.icon}
                      </div>
                      <h2 className={`text-xl font-bold mb-2 ${operation.textColor}`}>{operation.title}</h2>
                      <p className="text-gray-600 mb-4">{operation.description}</p>
                      <Button variant="ghost" className={`gap-2 ${operation.textColor}`}>
                        Selecionar <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
